import axios from "../lib/axios";
import { AxiosError } from "axios";

/**
 * Category interface
 */
export interface Category {
  _id?: string;
  code: string;
  name: string;
  createdAt?: string;
  updatedAt?: string;
  subCategory?: string[];
}

/**
 * Error Response interface
 */
interface ErrorResponse {
  message: string;
  statusCode?: number;
}

/**
 * Create category (single or bulk)
 */
export const createCategory = async (data: Category | Category[]) => {
  try {
    const response = await axios.post<{ message: string; data: Category | Category[] }>(
      "/category",
      data
    );
    return response.data.data;
  } catch (error) {
    const axiosError = error as AxiosError<ErrorResponse>;
    if (axiosError.response?.data) {
      throw new Error(axiosError.response.data.message || "Failed to create category");
    }
    throw new Error("Network error. Please try again.");
  }
};


/**
 * Get categories (list with pagination, search, sort)
 */
export const getCategories = async (params?: {
  page?: number;
  pageSize?: number;
  search?: string;
  sortOrder?: "asc" | "desc";
  categoryId?: string;
}) => {
  try {
    const response = await axios.get<{
      status: string;
      result:
      | Category[] // when categoryId is provided (single/multiple)
      | {
        pagination: {
          total: number;
          page: number;
          pageSize: number;
          totalPages: number;
        };
        data: Category[];
      };
    }>("/category", { params });

    const result = response.data.result;

    // 🟢 If result is an array → categoryId case
    if (Array.isArray(result)) {
      return {
        data: result,
        pagination: {
          total: result.length,
          page: 1,
          pageSize: result.length,
          totalPages: 1,
        },
      };
    }

    // 🟢 Otherwise → paginated list case
    return {
      data: result.data || [],
      pagination: result.pagination || {
        total: 0,
        page: 1,
        pageSize: 10,
        totalPages: 1,
      },
    };
  } catch (error) {
    const axiosError = error as AxiosError<ErrorResponse>;
    if (axiosError.response?.data) {
      throw new Error(
        axiosError.response.data.message || "Failed to fetch categories"
      );
    }
    throw new Error("Network error. Please try again.");
  }
};


/**
 * Get category by ID
 */
export const getCategoryById = async (id: string) => {
  try {
    const response = await axios.get<{
      status: string;
      result: Category;
    }>(`/category/${id}`);

    return response.data.result; // <- use result, not data
  } catch (error) {
    const axiosError = error as AxiosError<ErrorResponse>;
    if (axiosError.response?.data) {
      throw new Error(
        axiosError.response.data.message || "Failed to fetch category"
      );
    }
    throw new Error("Network error. Please try again.");
  }
};


/**
 * Update category by ID
 */
export const updateCategory = async (id: string, updateData: Partial<Category>) => {
  try {
    const response = await axios.put<{ message: string; data: Category }>(
      `/category/${id}`,
      updateData
    );
    return response.data.data;
  } catch (error) {
    const axiosError = error as AxiosError<ErrorResponse>;
    if (axiosError.response?.data) {
      throw new Error(axiosError.response.data.message || "Failed to update category");
    }
    throw new Error("Network error. Please try again.");
  }
};

/**
 * Delete category by ID
 */
export const deleteCategory = async (id: string) => {
  try {
    const response = await axios.delete<{ message: string }>(
      `/category/${id}`
    );
    return response.data.message;
  } catch (error) {
    const axiosError = error as AxiosError<ErrorResponse>;
    if (axiosError.response?.data) {
      throw new Error(axiosError.response.data.message || "Failed to delete category");
    }
    throw new Error("Network error. Please try again.");
  }
};
